import React, { useCallback, useState, useEffect, useRef, useContext } from 'react';
import { DataGrid, Column, FilterRow, Scrolling, SearchPanel, Button as GridButton, DataGridTypes } from 'devextreme-react/data-grid';

import { Button as StandardButton } from 'devextreme-react';

import ProcessingResult from '../../framework/ProcessingResult';

import CustomStore from 'devextreme/data/custom_store';
import GlobalLoader from '../loader/GlobalLoader';

import NotificationContext from '../../NotificationContext';

import { formatMessage } from 'devextreme/localization';

import AgentForm, { AgentFormHandle } from '../agent-form/AgentForm';
import DeleteConfirmationForm from '../delete-confirmation-form/DeleteConfirmationForm';
import { DeleteResult, useDeleteConfirmation } from '../delete-confirmation-form/useDeleteConfirmation';

import MessageForm, { MessageFormHandle, WarningMessage } from '../../components/message-form/MessageForm';

import CustomerAgentApi from '../../api/CustomerAgentApi';

import CustomerAgentUsageDto from "../../classes/dtos/customeragentmodule/CustomerAgentUsageDto";
import CustomerAgentListDto from '../../classes/dtos/customeragentmodule/CustomerAgentListDto';
import GetCustomerAgentsResult from '../../classes/api/result/customeragentmodule/GetCustomerAgentsResult';
import CustomerAgentStateChangeDto from '../../classes/dtos/notificationmodule/CustomerAgentStateChangeDto';


interface AgentGridProps {
}

const AgentGrid: React.FC<AgentGridProps> = () => {

    const context = useContext(NotificationContext);

    if (!context) {
        throw new Error("LastRuns must be used within a NotificationContextProvider");
    }

    const agentFormHandleRef = useRef<AgentFormHandle>(null);

    const messageFormHandleRef = useRef<MessageFormHandle>(null);

    const [agents, setAgents] = useState<Array<CustomerAgentListDto> | null>(null);

    const deleteItem = useCallback(async (item: CustomerAgentListDto): Promise<DeleteResult<CustomerAgentUsageDto[]>> => {

        let result = await CustomerAgentApi.DeleteCustomerAgentAsync(item.Id);

        if (result.IsFailed()) {
            return {
                success: false,
                message: result.ErrorMessage,
                result: null
            };
        }

        return {
            success: result.Result.Deleted,
            message: "",
            result: result.Result.UsageDetails
        }
    }, []);

    const onDeleteTerminated = useCallback(async (item: CustomerAgentListDto, deleteResult: DeleteResult<CustomerAgentUsageDto[]>): Promise<void> => {
        if (!deleteResult.success) {
            if (messageFormHandleRef.current === null) {
                alert("ERROR");
                return;
            }

            messageFormHandleRef.current.OpenComplex(CustomList(deleteResult.result ?? []), WarningMessage);
        } else {
            setAgents(null);
        }
    }, []);

    const deleteConfirmation = useDeleteConfirmation<CustomerAgentListDto, CustomerAgentUsageDto[]>(deleteItem, onDeleteTerminated);

    const CustomList = (usages: CustomerAgentUsageDto[]) => {
        const maxDisplay = 20;
        const displayedUsages = usages.slice(0, maxDisplay);
        const remainingCount = usages.length - maxDisplay;

        return (
            <div>
                {formatMessage("SettingsAgents_AgentIsUsed")}
                <ul>
                    {displayedUsages.map((e, index1) => (
                        <li key={index1}>{e.DataSourceName} ({e.DataSourceType})<ul>{e.DataSourceUsages.map((f, index2) => (<li key={index2}>{f.PathName} ({f.PathType})</li>))}</ul></li>
                    ))}
                </ul>
                {remainingCount === 1 && (
                    <div>
                        {formatMessage("SettingsAgents_And1Other")}
                    </div>
                )}
                {remainingCount > 1 && (
                    <div>
                        {formatMessage("SettingsAgents_AndXOthers", remainingCount.toString())}
                    </div>
                )}
            </div>
        );
    };

    function handleAgentAdded(dataSourceId: number) {
        setAgents(null);
    }

    const refresh = useCallback(() => {
        if (!agents) {
            CustomerAgentApi.GetCustomerAgentsAsync().then((allAgents: ProcessingResult<GetCustomerAgentsResult>) => {
                if (allAgents.IsFailed()) {
                    // TODO: Handle failure case (e.g., logout)
                } else {
                    setAgents(allAgents.Result.CustomerAgents);
                }
            });
        }
    }, [agents]);

    // UseEffect to refresh when component mounts
    useEffect(() => {
        refresh();
    }, [refresh]);

    function createAgent(): void {
        if (agentFormHandleRef.current !== null) {
            agentFormHandleRef.current.Open(null);
        }
    };

    function updateAgent(agentId: number): void {
        if (agentFormHandleRef.current !== null) {
            agentFormHandleRef.current.Open(agentId);
        }
    };

    async function deleteAgent(customerAgent: CustomerAgentListDto): Promise<void> {
        deleteConfirmation.showConfirm(customerAgent);
    }

    const customAgent = new CustomStore({
        key: 'Id',
        load: () => Promise.resolve(agents ?? [])
    });

    const renderRoles = (cellData: DataGridTypes.ColumnCellTemplateData) => {
        return (
            GetRolesLabel(cellData.data.Roles)
        );
    }

    function GetRolesLabel(Roles: number) {
        switch (Roles) {
            case 1:
                return formatMessage("SettingsAgentGridValue_LocalDataSource");
            case 3:
                return formatMessage("SettingsAgentGridValue_LocalDataSourceAndTaskRunner");
        }
        return "";
    }

    const renderState = (cellData: DataGridTypes.ColumnCellTemplateData) => {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
                {(cellData.data.State === 1) ?
                    <i className="dx-icon dx-icon-globe" style={{ color: '#00FF00' }} /> :
                    <i className="dx-icon dx-icon-globe" style={{ color: '#FF0000' }} />}
            </div>
        );
    }

    const handleStateChange = (payload: string) => {
        let dto: CustomerAgentStateChangeDto = JSON.parse(payload);

        setAgents((prevAgents) => {

            if (prevAgents !== null) {

                const line = prevAgents.find(e => e.Id === dto.CustomerAgentId);

                if (line != null) {
                    return prevAgents.map(agent =>
                        agent.Id === dto.CustomerAgentId ? { ...agent, State: dto.State } : agent
                    );
                }
            }

            return prevAgents;
        });
    };

    useEffect(() => {
        context.notificationConnection.on('CustomerAgentStateChange', handleStateChange);

        return () => {
            context.notificationConnection.off('CustomerAgentStateChange', handleStateChange);
        };
    }, []);

    if (!agents) {
        return <GlobalLoader />
    }

    return (
        <div className={"block-card"}>
            <div className={"block-card__header"}>
                <h2>{formatMessage("SettingsAgents_Title")}</h2>
                <StandardButton icon="plus" width={140} text={formatMessage("SettingsAgents_ButtonCreate")} type="default" stylingMode="contained" onClick={createAgent} disabled={false} />
            </div>
            <DataGrid
                id="dataGrid"
                dataSource={customAgent}
                allowColumnReordering={true}
                allowColumnResizing={true}
                columnAutoWidth={false}
                hoverStateEnabled={true}
                repaintChangesOnly={true}
                height='calc(100vh - 400px)'
                onRowUpdated={(e) => {
                    e.component.refresh();
                }}>
                <Scrolling mode="infinite" />
                <FilterRow visible={true} />
                <SearchPanel visible={false} />
                <Column caption={formatMessage("SettingsAgentGridColumn_Name")} dataField="Name" dataType="string" allowSorting={true} allowEditing={false} />
                <Column caption={formatMessage("SettingsAgentGridColumn_Roles")} dataField="Roles" dataType="string" allowSorting={true} allowEditing={false} cellRender={renderRoles} />
                <Column caption={formatMessage("SettingsAgentGridColumn_CreatedBy")} dataField="CreatedBy.Label" dataType="string" allowSorting={true} allowEditing={false} />
                <Column caption={formatMessage("SettingsAgentGridColumn_Token")} dataField="Token" dataType="string" allowSorting={true} allowEditing={false} />
                <Column caption={formatMessage("SettingsAgentGridColumn_State")} dataField="State" dataType="number" allowSorting={true} allowEditing={false} cellRender={renderState} width={"80px"} />
                <Column type="buttons">
                    <GridButton icon="edit" hint={formatMessage("SettingsAgentGrid_ButtonEdit")} onClick={(e) => { updateAgent(e.row?.data.Id) }} />
                    <GridButton icon="trash" hint={formatMessage("SettingsAgentGrid_ButtonDelete")} onClick={(e) => { deleteAgent(e.row?.data) }} />
                </Column>

            </DataGrid>
            <AgentForm ref={agentFormHandleRef} AgentAdded={handleAgentAdded} />

            <DeleteConfirmationForm
                visible={deleteConfirmation.isVisible}
                onConfirm={deleteConfirmation.handleConfirm}
                onCancel={deleteConfirmation.handleCancel}
                message={formatMessage("SettingsAgents_DeleteConfirmation", deleteConfirmation.itemToDelete?.Name ?? "")}
            />
            <MessageForm ref={messageFormHandleRef} />
        </div>
    );
};

export default AgentGrid;
